import { CheckIcon, XMarkIcon } from '@heroicons/react/16/solid'
import type { WizardStep } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IssueList, StepState } from '../../../Services/OrderWizard/issues.ts'
import { STEP_LABELS, stepStates } from '../../../Services/OrderWizard/issues.ts'

type Position = 'done' | 'current' | 'upcoming'

const problemText = (state: StepState): string | undefined =>
  state.state === 'fix' ? `${state.blockers} to fix` : state.state === 'check' ? `${state.warnings} to check` : undefined

const Marker = ({ state }: { state: StepState }) => {
  if (state.state === 'fix') {
    return (
      <span
        className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-600 text-white ring-2 ring-white"
        data-testid={`wizard-step-marker-${state.step}`}
        data-marker="fix"
      >
        <XMarkIcon className="h-2.5 w-2.5" />
      </span>
    )
  }
  if (state.state === 'check') {
    return (
      <span
        className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white"
        data-testid={`wizard-step-marker-${state.step}`}
        data-marker="check"
      />
    )
  }
  return null
}

const CIRCLE: Record<Position, string> = {
  current: 'border-primary bg-primary text-primary-content',
  done: 'border-slate-400 bg-white text-slate-700',
  upcoming: 'border-slate-300 bg-white text-slate-400'
}

const LABEL: Record<Position, string> = {
  current: 'font-semibold text-slate-900',
  done: 'hidden text-slate-700 sm:block',
  upcoming: 'hidden text-slate-400 sm:block'
}

/**
 * A step without problems shows a check, except Review, which is never done before submit; one with
 * problems a red or amber dot. On narrow screens only the current step keeps its label.
 */
export const WizardStepper = ({
  steps,
  current,
  issues,
  onSelect
}: {
  steps: WizardStep[]
  current: WizardStep
  issues: IssueList
  onSelect: (step: WizardStep) => void
}) => {
  const states = stepStates(issues, steps)
  const currentIndex = steps.indexOf(current)
  return (
    <nav aria-label="Order steps" data-testid="wizard-stepper">
      <ol className="flex items-start">
        {states.map((state, index) => {
          const position: Position = index === currentIndex ? 'current' : index < currentIndex ? 'done' : 'upcoming'
          const problem = problemText(state)
          const checked = position !== 'current' && state.state === 'ok' && state.step !== 'review'
          const label = STEP_LABELS[state.step]
          return (
            <li key={state.step} className="relative flex min-w-0 flex-1 flex-col items-center">
              {index > 0 && <span aria-hidden className="absolute right-1/2 top-4 h-px w-full bg-slate-200" />}
              <button
                type="button"
                aria-current={position === 'current' ? 'step' : undefined}
                aria-label={`${index + 1}. ${label}${problem ? `, ${problem}` : ''}`}
                title={problem ?? label}
                data-testid={`wizard-step-${state.step}`}
                data-state={state.state}
                data-position={position}
                onClick={() => onSelect(state.step)}
                className="group relative z-10 flex min-w-0 max-w-full flex-col items-center gap-1 rounded-md px-1 text-xs focus-visible:outline-2 focus-visible:outline-slate-400"
              >
                <span
                  className={`relative flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors ${CIRCLE[position]} ${
                    position === 'current' ? '' : 'group-hover:border-slate-500'
                  }`}
                  data-testid={`wizard-step-circle-${state.step}`}
                >
                  {checked ? <CheckIcon className="h-4 w-4" /> : index + 1}
                  <Marker state={state} />
                </span>
                <span className={`max-w-full truncate ${LABEL[position]}`}>{label}</span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
